import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

export default function Cancellation() {
  return (
    <div className="min-h-screen bg-bg text-ink" data-testid="cancellation-page">
      <header className="border-b border-line">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/" className="text-sm font-bold inline-flex items-center gap-1.5 text-ink-soft hover:text-brand"><ArrowLeft className="w-4 h-4"/> Back</Link>
          <img src="/brand/petbond-logo.png" alt="PetBond" className="h-10"/>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-5 py-12 prose prose-sm">
        <h1 className="font-heading text-4xl font-extrabold">Cancellation Policy</h1>
        <p className="text-ink-soft text-sm">Last updated: {new Date().toLocaleDateString("en-IN", { day: "numeric", month: "long", year: "numeric" })}</p>

        <p className="mt-4">This policy describes how you can cancel a subscription, appointment, hostel booking, order or account on PetBond.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">1. Premium subscription cancellation</h2>
        <ul>
          <li>Go to <Link to="/app/billing" className="text-brand font-bold">Billing → Subscription</Link> in your dashboard.</li>
          <li>Click <b>"Cancel subscription"</b>. You will retain premium access until the end of your paid billing cycle.</li>
          <li>Cancellations are effective immediately — no further amount will be charged.</li>
          <li>You can re-subscribe anytime from the same page.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">2. Vet appointment cancellation</h2>
        <ul>
          <li>Go to <Link to="/app/appointments" className="text-brand font-bold">My Appointments</Link>.</li>
          <li>Tap <b>"Cancel"</b> against the appointment. Free cancellation if 2+ hours before slot.</li>
          <li>Automatic notification is sent to the vet, freeing up the slot for another parent.</li>
          <li>Refund (if any) processed as per <Link to="/refund" className="text-brand font-bold">Refund Policy</Link>.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">3. Hostel & daycare booking cancellation</h2>
        <ul>
          <li>Go to <Link to="/app/hostels" className="text-brand font-bold">Hostels → My Bookings</Link>.</li>
          <li>Cancel from the booking card. Refund amount is calculated based on time-to-check-in — see <Link to="/refund" className="text-brand font-bold">Refund Policy §4</Link>.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">4. Store order cancellation</h2>
        <ul>
          <li>Orders can be cancelled from <Link to="/app/my-orders" className="text-brand font-bold">My Orders</Link> before the store marks them as "Shipped".</li>
          <li>Once shipped, the order cannot be cancelled but can be returned as per Refund Policy §3.</li>
          <li>Cash-on-delivery (COD) orders can be refused at delivery — no refund required.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">5. Account deletion</h2>
        <ul>
          <li>Go to <Link to="/app/privacy-data" className="text-brand font-bold">/app/privacy-data</Link> in your dashboard.</li>
          <li>Two options: <b>30-day grace deletion</b> (reversible) or <b>immediate hard deletion</b> (irreversible).</li>
          <li>Financial records are anonymised, not deleted, per the Indian Income Tax Act 6-year retention rule.</li>
          <li>Full details in our <Link to="/privacy" className="text-brand font-bold">Privacy Policy §7</Link>.</li>
        </ul>

        <h2 className="font-heading text-2xl font-extrabold mt-8">6. Vendor onboarding cancellation</h2>
        <p>Vets, stores, hostels and NGOs onboarding to PetBond may withdraw at any point before KYC verification with no charge. Post-KYC withdrawal requires a written request to <a href="mailto:hello@petbond.in">hello@petbond.in</a>; commissions from completed transactions remain payable.</p>

        <h2 className="font-heading text-2xl font-extrabold mt-8">7. Need help?</h2>
        <p>If any cancellation button doesn't work or you can't find the option, reach us:</p>
        <ul>
          <li>Email: <a href="mailto:hello@petbond.in">hello@petbond.in</a></li>
          <li>WhatsApp: <a href="https://wa.me/919819793270" target="_blank" rel="noreferrer">+91 98197 93270</a></li>
          <li>We respond within 24 hours (working days).</li>
        </ul>

        <p className="mt-8">See also: <Link to="/refund" className="text-brand font-bold">Refund Policy</Link> · <Link to="/terms" className="text-brand font-bold">Terms of Service</Link> · <Link to="/privacy" className="text-brand font-bold">Privacy Policy</Link></p>
      </main>
    </div>
  );
}
